import sentry from '../sentry';
import { useToast } from '@chakra-ui/react';
import React, { useContext, useEffect, useState } from 'react';
import CustomToast from '../common/CustomToast';
import { MeContext } from '../context/MeContext';
import { useGetMeQuery } from '../store/redux/api/accountApi';

function UseMe({ children }) {
  const { updateState, formatInvitations } = useContext(MeContext);
  const { data, isLoading, isError, error } = useGetMeQuery();
  const toast = useToast();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!data) {
      setIsReady(false);
      return;
    }

    const account = data.account || null;
    const impersonation = sessionStorage.getItem('impersonation');
    const invitations = data.invitations;
    const formattedInvitations = formatInvitations(invitations);

    updateState({
      account: { ...account },
      organisationStatus: data.organisation_status,
      trialExpiry: data.trial_expiry,
      watermarkEnabled: data.watermark_enabled,
      watermarkFree: data.watermark_free,
      entities: data.entities,
      passTemplates: data.pass_templates,
      impersonation,
      invitations: formattedInvitations,
      invitationLimit: account?.invitation_limit,
      eventsEnabled: data.events_enabled,
      filterEntity: data.filter_entity,
      vtapAutomaticSetupEnabled: data.vtap_automatic_setup_enabled,
      subscribed: data.subscribed,
      subscriptionRequired: data.subscription_required,
      status: 'resolved',
      error: null,
      smsAndEmailMessagingEnabled: data.sms_and_email_messaging_enabled,
      newsletterPopUpAccepted: data.newsletter_pop_up_accepted,
      billingEnabled: data.billing_enabled,
      languagesSectionEnabled: data.languages_section_enabled,
      builderToggleEnabled: data.builder_toggle_enabled,
      localisationEnabled: data.localisation_enabled,
      cardlessTrialStartedAt: data.cardless_trial_started_at,
      cardlessTrialEnabled: data.cardless_trial_enabled,
      passBindingEnabled: data.pass_binding_enabled,
      appleEnhancedDesignEnabled: data.apple_enhanced_design_enabled,
      passBuilderEnabled: data.pass_builder_enabled,
      passBuilderEnabledInternal: data.pass_builder_enabled_internal,
      onboardingChecklistEnabled: data.onboarding_checklist_enabled,
      onboardingCompleted: data.onboarding_completed,
      onboardingQuestionnaireEnabled: data.onboarding_questionnaire_enabled,
      onboardingQuestionnaireCompleted: data.onboarding_questionnaire_completed,
      prebuiltTemplatesEnabled: data.prebuilt_templates_enabled,
      firstPassImprovementsEnabled: data.first_pass_improvements_enabled,
      expressivePassRolloutEnabled: data.expressive_pass_rollout_enabled,
      readOnlyDashboardEnabled: data.read_only_dashboard_enabled,
      primaryUseCase: data.primary_use_case,
      industry: data.industry,
      messagingRedesignEnabled: data.messaging_redesign_enabled,
      expressivePassRevertButtonEnabled:
        data.expressive_pass_revert_button_enabled,
    });
    setIsReady(true);
  }, [data]);

  useEffect(() => {
    if (!isError || !error) return;
    sentry.sendError(error);

    const errors = error?.data?.errors;
    if (errors && Array.isArray(errors)) {
      errors.forEach((err) => {
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title={err}
              description=""
              onClose={props.onClose}
            />
          ),
        });
      });
    } else {
      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="An error occurred"
            description={error?.data?.message || 'Failed to fetch user data'}
            onClose={props.onClose}
          />
        ),
      });
    }

    updateState({
      account: null,
      organisationStatus: null,
      trialExpiry: null,
      watermarkEnabled: null,
      watermarkFree: null,
      entities: null,
      invitations: null,
      invitationLimit: null,
      eventsEnabled: null,
      filterEntity: null,
      subscribed: null,
      subscriptionRequired: null,
      status: 'rejected',
      error: 'Something went wrong',
      vtapAutomaticSetupEnabled: null,
      impersonation: null,
      billingEnabled: null,
      smsAndEmailMessagingEnabled: null,
      newsletterPopUpAccepted: null,
      languagesSectionEnabled: null,
      builderToggleEnabled: null,
      localisationEnabled: null,
      cardlessTrialStartedAt: null,
      cardlessTrialEnabled: null,
      passBindingEnabled: null,
      appleEnhancedDesignEnabled: null,
      passBuilderEnabled: null,
      passBuilderEnabledInternal: null,
      onboardingChecklistEnabled: null,
      onboardingCompleted: null,
      onboardingQuestionnaireEnabled: null,
      onboardingQuestionnaireCompleted: null,
      prebuiltTemplatesEnabled: null,
      firstPassImprovementsEnabled: null,
      expressivePassRolloutEnabled: null,
      readOnlyDashboardEnabled: null,
      primaryUseCase: null,
      industry: null,
      messagingRedesignEnabled: null,
    });
  }, [isError, error]);

  if (isLoading || !isReady) return null;
  return <>{children}</>;
}

export default UseMe;
